import { useNavigate } from '@tanstack/react-router'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { ReactNode } from 'react'
import { GeckoGeographicPin, RolodexEntryType } from 'types'

/**
 * A function that returns interaction-actions for geographic pins (click handler, and image to display on hover.
 * @param pin
 */
export function useGetGeographicPinInteractionHandler(pin: GeckoGeographicPin) {
  const handleClick = useGetGeographicPinHandleClick(pin)
  const hoverImage = useGetGeographicPinImage(pin)

  return {
    handleClick,
    hoverImage,
  }
}

// ----------------------------------------------------------------------------------------------------
function useGetGeographicPinImage(
  pin: GeckoGeographicPin
): ReactNode | undefined {
  const { url: thumbnailURL } = useRolodexSignedURLs({
    entry_id: pin.id,
    path: 'thumbnail.png',
  })

  if (!thumbnailURL) {
    return undefined
  }

  return <img src={thumbnailURL} alt={pin.data.name ?? pin.id} />
}
// ----------------------------------------------------------------------------------------------------
/**
 * A function that returns a click handler for geographic pins.
 */
function useGetGeographicPinHandleClick(pin: GeckoGeographicPin) {
  const navigate = useNavigate()

  const { fileRefs: thumbnailRefs } = useRolodexFileLS({
    entry_id: pin.id,
    path: 'thumbnail.png',
  })

  const thumbnailExists = thumbnailRefs.length > 0
  const potreePointCloud = pin.tags[RolodexEntryType.POTREE_POINT_CLOUD]

  const pointCloudStartTarget =
    pin.data.geo_json.properties?.point_cloud_start_target
  const pointCloudStartPosition =
    pin.data.geo_json.properties?.point_cloud_start_position

  const handleClick = (): React.ReactNode => {
    const searchParams: { cameraPos?: string; cameraTarget?: string } = {}

    if (!potreePointCloud || !thumbnailExists) {
      return null
    }

    if (
      pointCloudStartPosition &&
      Array.isArray(pointCloudStartPosition) &&
      pointCloudStartPosition.length === 3
    ) {
      searchParams.cameraPos = pointCloudStartPosition.join(',')
    }

    if (
      pointCloudStartTarget &&
      Array.isArray(pointCloudStartTarget) &&
      pointCloudStartTarget.length === 3
    ) {
      searchParams.cameraTarget = pointCloudStartTarget.join(',')
    }

    navigate({
      to: '/point-cloud/' + pin.tags.site + '/' + potreePointCloud,
      search: searchParams,
    })
    return null
  }

  return handleClick
}
